import { useEffect, useRef, useState, type ReactNode } from 'react'
import { TagIcon } from 'lucide-react'
import type { MoodboardPolarity, MoodboardRole } from '@backend/lib/db/schema/moodboard'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover'
import { Label } from '@/components/ui/shadcn/label'
import { Textarea } from '@/components/ui/shadcn/textarea'

type Props = {
  polarity: MoodboardPolarity | undefined
  annotation: string | undefined
  onChange: (patch: {
    polarity?: MoodboardPolarity | undefined
    annotation?: string | undefined
    role?: MoodboardRole | undefined
  }) => Promise<void> | void
  // Item-type-specific metadata rendered as a footer inside the popover (e.g., scraped
  // status, file size, hex code). Each card supplies its own.
  children: ReactNode
  // Optional item-type-specific edit controls (e.g., color picker for the color item).
  // Rendered above the info footer, between annotation and the muted divider.
  editor?: ReactNode
  // When set, show the role picker (brief vs content). Only URL items support `content`
  // today, so only the link card opts in. `role` is the item's current value.
  role?: MoodboardRole | undefined
  roleEditable?: boolean
}

const ROLE_OPTIONS: { value: MoodboardRole; label: string; hint: string }[] = [
  { value: 'brief', label: 'Brand voice', hint: 'Distilled into the brief once' },
  { value: 'content', label: 'Fresh for posts', hint: 'Fetched live at generation' },
]

// Single per-item affordance pinned top-right. One click opens a Popover that bundles:
//   - Polarity picker (4-way segmented)
//   - Annotation textarea
//   - A muted footer section with item-type-specific info (the caller's children)
// Polarity tints the trigger so non-default state is visible without opening the popover.

const POLARITY_OPTIONS: {
  value: MoodboardPolarity
  label: string
  ringClass: string
  selectedClass: string
}[] = [
  {
    value: 'reference',
    label: 'Reference',
    ringClass: 'ring-border/60',
    selectedClass: 'bg-muted text-foreground ring-border/80 ring-2',
  },
  {
    value: 'hero',
    label: 'Hero',
    ringClass: 'ring-emerald-500/60',
    selectedClass:
      'bg-emerald-500/15 text-emerald-700 ring-emerald-500/60 ring-2 dark:text-emerald-300',
  },
  {
    value: 'competitor',
    label: 'Competitor',
    ringClass: 'ring-amber-500/60',
    selectedClass: 'bg-amber-500/15 text-amber-700 ring-amber-500/60 ring-2 dark:text-amber-300',
  },
  {
    value: 'antiPattern',
    label: 'Avoid',
    ringClass: 'ring-rose-500/60',
    selectedClass: 'bg-rose-500/15 text-rose-700 ring-rose-500/60 ring-2 dark:text-rose-300',
  },
]

// Trigger tint per polarity. Subtle accent on the corner button so the user can read the
// item's state at a glance.
const TRIGGER_TINT: Record<MoodboardPolarity, string> = {
  reference: 'bg-background/80 text-muted-foreground hover:text-foreground',
  hero: 'bg-emerald-500/20 text-emerald-700 hover:text-emerald-800 dark:text-emerald-300',
  competitor: 'bg-amber-500/20 text-amber-700 hover:text-amber-800 dark:text-amber-300',
  antiPattern: 'bg-rose-500/20 text-rose-700 hover:text-rose-800 dark:text-rose-300',
}

export function ItemInfoBadge({
  polarity,
  annotation,
  onChange,
  editor,
  children,
  role,
  roleEditable,
}: Props) {
  const effective = polarity ?? 'reference'
  const effectiveRole = role ?? 'brief'
  const isContent = roleEditable === true && effectiveRole === 'content'
  const hasAnnotation = !!annotation && annotation.length > 0

  // Local annotation draft so typing doesn't fire a tRPC mutation per keystroke.
  // Commit on blur / popover close.
  const [annotationDraft, setAnnotationDraft] = useState<string | null>(null)
  const draftValue = annotationDraft ?? annotation ?? ''

  // Controlled open state. Radix's built-in outside-click detection uses a
  // standard event listener which gets stopped by the card drag handler's
  // `e.stopPropagation()` (needed to keep canvas marquee from also firing).
  // We replace it with a capture-phase listener on document so we see the
  // pointerdown before React's synthetic propagation can block it.
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null
      if (!target) return
      if (triggerRef.current?.contains(target)) return
      if (contentRef.current?.contains(target)) return
      // Nested popovers (e.g., the colour picker in StickyNote's editor) portal
      // their content outside `contentRef`, so a click inside them would
      // normally close us. Keep open as long as the click is in any Radix
      // popover wrapper — Radix handles its own close logic per layer.
      if (target.closest('[data-radix-popper-content-wrapper]')) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [open])

  const commitAnnotation = () => {
    if (annotationDraft === null) return
    const next = annotationDraft.trim() || undefined
    setAnnotationDraft(null)
    if (next !== (annotation || undefined)) {
      void onChange({ annotation: next })
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) commitAnnotation()
      }}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Item annotation"
          className={[
            // Visible only on hover when nothing is set; persistent when polarity or
            // annotation has been touched so the state stays readable.
            'absolute right-2 top-2 z-10 rounded-full p-1 shadow-sm transition-opacity data-[state=open]:opacity-100',
            TRIGGER_TINT[effective],
            // A content-role item stays marked so its "fetched fresh" state is readable
            // without opening the popover.
            isContent ? 'ring-1 ring-indigo-500/60' : '',
            effective === 'reference' && !hasAnnotation && !isContent
              ? 'opacity-0 group-hover:opacity-100'
              : 'opacity-100',
          ].join(' ')}>
          <TagIcon size={11} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        side="bottom"
        align="end"
        sideOffset={6}
        className="w-72 overflow-hidden"
        onPointerDown={(e) => e.stopPropagation()}>
        {/* `grid-cols-1` (which sets minmax(0,1fr)) prevents wide children
            like the scraped-content <pre> from stretching the popover past
            its intended `w-72` — grid items otherwise default to min-content
            width, which a long unbreakable run will blow out. */}
        <div className="grid grid-cols-1 gap-3">
          {roleEditable ? (
            <div>
              <Label className="text-muted-foreground mb-1.5 block text-[10px] uppercase tracking-wide">
                Usage
              </Label>
              <div className="grid grid-cols-2 gap-1">
                {ROLE_OPTIONS.map((opt) => {
                  const selected = opt.value === effectiveRole
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => void onChange({ role: opt.value })}
                      title={opt.hint}
                      className={[
                        'rounded-md border px-2 py-1.5 text-left text-xs transition-colors',
                        selected
                          ? 'bg-indigo-500/15 text-indigo-700 ring-2 ring-indigo-500/60 dark:text-indigo-300'
                          : 'border-border/60 text-foreground hover:bg-hover',
                      ].join(' ')}>
                      {opt.label}
                    </button>
                  )
                })}
              </div>
            </div>
          ) : null}
          <div>
            <Label className="text-muted-foreground mb-1.5 block text-[10px] uppercase tracking-wide">
              Polarity
            </Label>
            <div className="grid grid-cols-2 gap-1">
              {POLARITY_OPTIONS.map((opt) => {
                const selected = opt.value === effective
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => void onChange({ polarity: opt.value })}
                    className={[
                      'rounded-md border px-2 py-1.5 text-left text-xs transition-colors',
                      selected
                        ? opt.selectedClass
                        : 'border-border/60 text-foreground hover:bg-hover',
                    ].join(' ')}>
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div>
            <Label className="text-muted-foreground mb-1.5 block text-[10px] uppercase tracking-wide">
              Annotation
            </Label>
            <Textarea
              value={draftValue}
              onChange={(e) => setAnnotationDraft(e.target.value)}
              onBlur={commitAnnotation}
              rows={3}
              placeholder="Optional note to the analyzer about this item"
              className="text-xs"
            />
          </div>
          {editor}
          <div className="border-border/60 text-muted-foreground border-t pt-3 text-xs leading-snug">
            {children}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
